import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
    getMfaStatus,
    getSessions,
    getDevices,
    getPasskeys,
    getRecoveryCodesStatus,
    getSecurityEvents,
    getDastineCredentials,
} from '../api/securityApi';
import { SecurityEvent } from '../models/SecurityModels';

export const SECURITY_KEYS = {
    mfaStatus: 'security-mfa-status',
    sessions: 'security-sessions',
    devices: 'security-devices',
    passkeys: 'security-passkeys',
    recoveryCodes: 'security-recovery-codes',
    events: 'security-events',
    dastine: 'security-dastine',
};

/**
 * Loads all Security Center widgets (MFA status, sessions, devices) and
 * conditionally the sections that depend on MFA being enabled (passkeys,
 * recovery codes). Security events are always loaded.
 */
export const useSecurity = () => {
    const queryClient = useQueryClient();

    const mfaStatusQuery = useQuery({
        queryKey: [SECURITY_KEYS.mfaStatus],
        queryFn: async () => getMfaStatus(),
        staleTime: 30 * 1000,
    });

    const mfaEnabled = !!mfaStatusQuery.data?.hasMfa;
    const mfaMethods = mfaStatusQuery.data?.methods || [];

    const sessionsQuery = useQuery({
        queryKey: [SECURITY_KEYS.sessions],
        queryFn: async () => getSessions(),
        staleTime: 30 * 1000,
    });

    const devicesQuery = useQuery({
        queryKey: [SECURITY_KEYS.devices],
        queryFn: async () => getDevices(),
        staleTime: 30 * 1000,
    });

    const passkeysQuery = useQuery({
        queryKey: [SECURITY_KEYS.passkeys],
        queryFn: async () => getPasskeys(),
        staleTime: 30 * 1000,
        enabled: mfaEnabled,
    });

    const recoveryCodesQuery = useQuery({
        queryKey: [SECURITY_KEYS.recoveryCodes],
        queryFn: async () => getRecoveryCodesStatus(),
        staleTime: 30 * 1000,
        enabled: mfaEnabled,
    });

    const dastineQuery = useQuery({
        queryKey: [SECURITY_KEYS.dastine],
        queryFn: async () => getDastineCredentials(),
        staleTime: 30 * 1000,
        enabled: mfaEnabled,
    });

    const eventsQuery = useQuery({
        queryKey: [SECURITY_KEYS.events],
        queryFn: async () => getSecurityEvents({ limit: 20 }),
        staleTime: 60 * 1000,
    });

    useEffect(() => {
        if (mfaEnabled) {
            queryClient.invalidateQueries({ queryKey: [SECURITY_KEYS.passkeys] });
            queryClient.invalidateQueries({ queryKey: [SECURITY_KEYS.recoveryCodes] });
            queryClient.invalidateQueries({ queryKey: [SECURITY_KEYS.dastine] });
        }
    }, [mfaEnabled, queryClient]);

    const refreshAll = () => {
        queryClient.invalidateQueries({ queryKey: [SECURITY_KEYS.mfaStatus] });
        queryClient.invalidateQueries({ queryKey: [SECURITY_KEYS.sessions] });
        queryClient.invalidateQueries({ queryKey: [SECURITY_KEYS.devices] });
        queryClient.invalidateQueries({ queryKey: [SECURITY_KEYS.passkeys] });
        queryClient.invalidateQueries({ queryKey: [SECURITY_KEYS.recoveryCodes] });
        queryClient.invalidateQueries({ queryKey: [SECURITY_KEYS.dastine] });
        queryClient.invalidateQueries({ queryKey: [SECURITY_KEYS.events] });
    };

    return {
        mfaStatus: mfaStatusQuery.data,
        mfaEnabled,
        mfaMethods,
        sessions: sessionsQuery.data || [],
        sessionsLoading: sessionsQuery.isLoading,
        devices: devicesQuery.data || [],
        devicesLoading: devicesQuery.isLoading,
        passkeys: passkeysQuery.data || [],
        passkeysLoading: passkeysQuery.isLoading,
        dastineCredentials: dastineQuery.data || [],
        dastineLoading: dastineQuery.isLoading,
        recoveryCodesInfo: recoveryCodesQuery.data || null,
        events: (eventsQuery.data || []).map((e) => new SecurityEvent(e)),
        eventsLoading: eventsQuery.isLoading,
        isLoading:
            mfaStatusQuery.isLoading ||
            sessionsQuery.isLoading ||
            devicesQuery.isLoading ||
            (mfaEnabled && (passkeysQuery.isLoading || recoveryCodesQuery.isLoading || dastineQuery.isLoading)),
        refreshAll,
    };
};

export default useSecurity;